import { formatDate, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AutoCompleteComponent, AutoCompleteModule, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { FilterService, FilterSettingsModel, GridModule, GroupService, PageService, PageSettingsModel, SelectionSettingsModel, SortService } from '@syncfusion/ej2-angular-grids';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { DialogType, HtmlEditorService, ImageService, LinkService, RichTextEditorComponent, RichTextEditorModule, ToolbarService, ToolbarSettingsModel } from '@syncfusion/ej2-angular-richtexteditor';
import { MessageStackService } from '../../services/message-stack.service';
import { TicketService } from '../../services/ticket.service';
import { LoaderComponent } from '../loader/loader.component';

/**
 * Ticket list view: the landing screen of the help desk.
 *
 * Owns the Syncfusion grid of tickets plus the status filter, sort, search,
 * server-side paging and the new-ticket dialog. Opening a ticket routes to
 * `/ticket/<id>`.
 *
 * Listing state is server-driven: `next`/`previous` URLs returned by the API are
 * held in {@link nextBtn} and {@link prevBtn} and followed on demand. Status
 * filtering and sorting use separate endpoints, so {@link showSortGrid} records
 * which mode is active and therefore which paging calls apply.
 */
@Component({
  selector: 'app-help-desk',
  imports: [GridModule, LoaderComponent, DialogModule, TextBoxModule, RichTextEditorModule, DropDownListModule, FormsModule, AutoCompleteModule, NgIf, NgTemplateOutlet],
  providers: [PageService, SortService, FilterService, GroupService, ToolbarService, LinkService, ImageService, HtmlEditorService],
  templateUrl: './help-desk.component.html',
  styleUrl: './help-desk.component.css'
})
export class HelpDeskComponent implements OnInit {
  /** Grid filter configuration; uses the Excel-style filter menu. */
  public filterOptions: FilterSettingsModel = { type: 'Excel' };
  /** Client-side grid paging options for the ticket grid. */
  public pageSettings: PageSettingsModel = { pageSizes: ['All', 5, 10, 20, 50], pageSize: 10 };
  /** Grid row-selection configuration. */
  public selectionOptions?: SelectionSettingsModel;
  /** Whether a listing request is in flight; drives the skeleton loader. */
  public isLoading: boolean = true;
  /** Whether the new-ticket dialog is open. */
  public addNewTicket: boolean = false;
  /** Tickets currently shown in the grid, with dates already formatted for display. */
  public data: Object[] = [];
  /** Sort choices offered in the dropdown; each value is a `field-direction` pair. */
  public sortOptions: { text: string, value: string, category: string }[] = [
    { text: "Latest First", value: 'ticket_id-desc', category: 'Ticket ID' },
    { text: "Oldest First", value: 'ticket_id-asc', category: 'Ticket ID' },
    { text: "Latest First", value: 'last_updated-desc', category: 'Last Updated' },
    { text: "Oldest First", value: 'last_updated-asc', category: 'Last Updated' }
  ]
  /** Rendered width of the sort dropdown. */
  dropdownWidth: string = '100px'
  /** Currently selected sort option, as a `field-direction` string. */
  selectedSortFilter = ''
  /** Ticket status options for the status filter dropdown. */
  public statusData: { text: string; value: string }[] = [
    { text: "All", value: "All" },
    { text: "In-Progress", value: "In-Progress" },
    { text: "Answered", value: "Answered" },
    { text: "Resolved", value: "Resolved" },
    { text: "Reopen", value: "Reopen" },
    { text: "Closed", value: "Closed" },
    { text: "Parked", value: "Parked" },
    { text: "Unassigned", value: "Unassigned" }
  ]
  /** Issue category options offered on the new-ticket form. */
  public issueData: { text: string; value: string }[] = [
    { text: 'General Inquiry', value: 'General Inquiry' },
    { text: 'Legal Issue', value: 'Legal Issue' },
    { text: 'Sales Issue', value: 'Sales Issue' },
    { text: 'Technical Issue', value: 'Technical Issue' }
  ]
  /** Current text in the ticket search box. */
  searchBoxText: string = ''
  /** Subject field of the new-ticket form. */
  ticketSubject: string = ''
  /** Body of the new-ticket form, as HTML from the rich text editor. */
  ticketMessage: string = ''
  /** Raw semicolon-separated CC addresses typed on the new-ticket form. */
  participants: string = ''
  /** Selected issue category on the new-ticket form. */
  ticketIssue: string = ''
  /** Status the listing is currently filtered by. */
  selectedStatus: string = 'In-Progress'
  /** URL of the next page from the last listing response; empty when on the last page. */
  nextBtn: string = '';
  /** URL of the previous page from the last listing response; empty when on the first page. */
  prevBtn: string = '';
  /** Current page number, tracked locally since the API returns only next/previous URLs. */
  page: number = 1
  /** Rows requested per page. */
  page_size: number = 25;
  /** Display name of the signed-in user, read from `sessionStorage`. */
  userName: string = '';
  /** Whether the logout confirmation popup is open. */
  openLogoutPopup: boolean = false;
  /** Whether a ticket is being created; disables the submit button. */
  isSubmitting: boolean = false;
  /** Files staged as attachments on the new-ticket form. */
  uploadedFilesArray: File[] = [];
  /** Email address of the signed-in user, read from `sessionStorage`. */
  userEmail = ''
  /** Validation message for the subject field. */
  ticketSubjectErrorMessage: string = '';
  /** Validation message for the CC field. */
  participantsErrorMessage: string = '';
  /** Validation message for the message body. */
  ticketMessageErrorMessage: string = '';
  /** Set when the opened ticket is not assigned to the current user. */
  ticketNotAssigned: boolean = false;
  /** Total tickets matching the active status filter, from the response `count`. */
  totalCountPerStatus: number = 0;
  /** Set when the search term contains characters outside the allowed set. */
  searchTextError: boolean = false;
  /**
   * Attachment allow-list mapping file extension to its expected MIME type.
   *
   * Both must agree for a file to be accepted, so a renamed extension is rejected.
   */
  ALLOWED_FILES: { [key: string]: string } = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    bmp: "image/bmp",
    tiff: "image/tiff",
    tif: "image/tiff",
    webp: "image/webp",
    svg: "image/svg+xml",
    ico: "image/x-icon",
    heic: "image/heic",
    heif: "image/heif",
    jfif: "image/jpeg",
    pjpeg: "image/pjpeg",
    pjp: "image/pjpeg",
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    txt: "text/plain",
    csv: "text/csv",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    aac: "audio/aac",
    flac: "audio/flac",
    m4a: "audio/mp4",
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    mkv: "video/x-matroska",
    webm: "video/webm",
    zip: "application/zip",
    rar: "application/vnd.rar",
    "7z": "application/x-7z-compressed",
    tar: "application/x-tar",
    gz: "application/gzip",
  };
  /** Set when ticket creation is rejected with HTTP 429. */
  rateLimitingError = false;
  /** Set when the backend does not recognise the signed-in user as a customer. */
  noCustomerFound = false;
  /** True while a sorted listing is active, selecting the sorted paging endpoints. */
  showSortGrid: boolean = false;
  /** Field the listing is sorted on, e.g. `ticket_id`; resent with every sorted page. */
  filter: string = '';
  /** Sort direction, `asc` or `desc`; resent with every sorted page. */
  order: string = '';
  /** Toolbar layout for the new-ticket rich text editor. */
  public tools: ToolbarSettingsModel = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
      'FontName', 'FontSize', 'FontColor', '|',
      'Alignments', 'OrderedList', 'UnorderedList', '|',
      'Indent', 'Outdent', '|',
      'CreateLink', 'Image', 'CreateTable', '|',
      'FullScreen'
    ], 
  };
  /**
   * Reference to the participants autocomplete.
   *
   * Note: no `#autocomplete` template reference currently exists, so this is never
   * populated.
   */
  @ViewChild('autocomplete') autocomplete!: AutoCompleteComponent;
  /** Hidden file input, clicked programmatically by {@link triggerFileInput}. */
  @ViewChild('fileInput') fileInput: any;
  /** Rich text editor instance for the new-ticket body. */
  @ViewChild('rte') public rteObj!: RichTextEditorComponent;
  /** Populated from API error (HTTP 400 `error.results`); displayed in the template as a validation message. */
  errorMessage: string = '';
  
  /** Opens the new-ticket dialog. */
  openTicketDetails() {
    this.addNewTicket = true
  }

  /** Opens the OS file picker by clicking the hidden file input. */
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Uploads an image chosen through the editor toolbar and inserts it at the caret.
   *
   * What goes into the editor is a local `blob:` preview of the picked file, so
   * the writer can see what they inserted; the storage account the upload returns
   * refuses anonymous reads, so that URL cannot be displayed while composing. The
   * upload URL is tagged onto the image as `data-send-src` and `TicketService`
   * swaps it back into `src` on the way out, so only the uploaded URL is sent.
   *
   * The editor's own insert is cancelled first: it embeds a second copy of the
   * same file as a `blob:` URL, which dies with the page, so one pick produced
   * two images and the blob one broke on the next load. Cancelling also stops
   * the dialog closing itself, so close it here.
   *
   * @param args Syncfusion image-select event; the file is read from `filesData[0]`.
   */
  onImageSelected(args: any): void {
    args.cancel = true;
    this.rteObj.closeDialog(DialogType.InsertImage);

    const file = args.filesData[0].rawFile;

    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      console.error('Missing auth token');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${authToken}`
    });

    this.http.post<any>(
      `${this.ticketService.ticketAPI}/inline_files_upload/`,
      formData,
      { headers }
    ).subscribe({
      next: (res) => {
        this.rteObj.focusIn();
        const uploadedUrl = res.files?.[0];
        if (uploadedUrl) {
          const previewUrl = URL.createObjectURL(file);
          const imageHtml = `<img src="${previewUrl}" 
                            data-send-src="${uploadedUrl}" 
                            alt="Uploaded Image" 
                            style="
                              max-width: 500px !important;
                              min-width: 100px !important;
                              height: auto !important;
                              display: block !important;
                              margin: 10px 0 !important;
                              border-radius: 4px !important;"
                            >`;

          this.rteObj.executeCommand('insertHTML', imageHtml);
        } else {
          console.error('Upload succeeded but no image URL returned.');
        }
      },
      error: (err) => {
        console.error('Image upload failed:', err);
      }
    });
  }

  /** Validation message for attachment type and total-size failures. */
  fileUploadErrorMessage: string = '';

  /**
   * Validates newly picked attachments and appends the acceptable ones.
   *
   * A file is accepted only when its extension appears in {@link ALLOWED_FILES} and
   * its reported MIME type matches that entry. The batch is rejected outright if
   * any file fails, or if the combined size of new and existing attachments would
   * exceed 25 MB.
   *
   * @param event Change event from the hidden file input.
   */
  handleFileSelect(event: any) {
    this.fileUploadErrorMessage = '';

    const files = Array.from(event.target.files) as File[];
    const maxTotalSize = 25 * 1024 * 1024;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mime = file.type;

      if (ext && this.ALLOWED_FILES[ext] && this.ALLOWED_FILES[ext] === mime) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      this.fileUploadErrorMessage = `You have uploaded an invalid file. Please check the file-extension of the \n${invalidFiles.join(', ')}.`;
      return;
    }

    const currentTotalSize = this.uploadedFilesArray.reduce((sum, file) => sum + file.size, 0);
    const newFilesTotalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    const combinedTotalSize = currentTotalSize + newFilesTotalSize;

    if (combinedTotalSize > maxTotalSize) {
      this.fileUploadErrorMessage = 'Total attachments size must not exceed 25 MB.';
      return;
    }

    this.uploadedFilesArray = [...this.uploadedFilesArray, ...validFiles];
    this.fileUploadErrorMessage = '';
  }


  /**
   * Removes one staged attachment and re-checks the remaining total size.
   *
   * Note: the threshold checked here is 20 MB, while {@link handleFileSelect} adds
   * files against 25 MB and the message shown still reads 25 MB.
   *
   * @param file Attachment to remove.
   */
  removeFileFromUploadedArray(file: File) {
    this.uploadedFilesArray = this.uploadedFilesArray.filter(f => f !== file);

    const maxTotalSize = 20 * 1024 * 1024;
    const totalSize = this.uploadedFilesArray.reduce((sum, f) => sum + f.size, 0);

    if (totalSize > maxTotalSize) {
      this.fileUploadErrorMessage = 'Total attachments size must not exceed 25 MB.';
    } else {
      this.fileUploadErrorMessage = '';
    }
  }

  /**
   * Runs a ticket search, replacing the grid contents with the matches.
   *
   * The term is rejected before any request when it contains anything outside
   * letters, digits, underscore and space.
   */
  submitSearchForm() {
    this.searchTextError = false
    this.isLoading = true
    const searchTextLower = this.searchBoxText.toLowerCase();
    const validate = /^[a-zA-Z0-9_ ]+$/;
    if (validate.test(searchTextLower)) {
       this.ticketService.searchTicket(searchTextLower).subscribe({
      next: (response) => {
        this.isLoading = false
        // The pager reads its page count and "x - y of z" line off this, so a
        // search that leaves it alone keeps reporting the unfiltered total.
        this.totalCountPerStatus = response.count
        this.prevBtn = response.previous
        this.nextBtn = response.next
        this.data = this.formatRows(response.results);
        this.page = 1
      },
      error: (err) => {
        this.data = []
        // Same reason as above: the rows are gone, so the count has to go too,
        // or the pager advertises results that are no longer on screen.
        this.totalCountPerStatus = 0
        this.isLoading = false
        console.error(err)
      }
    })
  } else {
    this.searchTextError = true
    this.isLoading = false
  }
  }

  /**
   * Reloads the first page at a new page size while a sorted listing is active.
   *
   * @param event Change event from the page-size selector.
   */
  onPageSizeChangeSort(event: any) {
    this.page_size = Number(event.target.value);
    this.ticketService.sortTicketsData(this.filter, this.order, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.prevBtn = response.previous
        this.nextBtn = response.next
        this.data = this.formatRows(response.results);
        this.page = 1

      },
      error: (error) => {
        console.error(error)
      }
    })
  
  }

  /**
   * Reloads the first page at a new page size for the current status filter.
   *
   * @param event Change event from the page-size selector.
   */
  onPageSizeChange(event: any) {
    this.page_size = Number(event.target.value);
    this.ticketService.getTicketsData(this.selectedStatus, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.prevBtn = response.previous
        this.nextBtn = response.next
        this.data = this.formatRows(response.results);
        this.page = 1


      },
      error: (error) => {
        this.data = []
        this.isLoading = false
        console.error(error)
      }
    })
  }

  /** Clears the search box and restores the listing for the active status filter. */
  clearSearchForm() {
    this.searchTextError = false
    this.searchBoxText = ''
    this.isLoading = true
    this.ticketService.getTicketsData(this.selectedStatus, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.data = this.formatRows(response.results);
        this.page = 1
      },
      error: (error) => {
        this.data = []
        this.isLoading = false
        console.error(error)
      }
    })
  }

  /** Closes the new-ticket dialog. */
  closePopup() {
    this.addNewTicket = false;
  }

  /**
   * Validates and submits the new-ticket form.
   *
   * Subject, CC addresses and body are checked in that order and the first failure
   * wins; submission proceeds only when no field carries an error. The dialog is
   * cleared and closed only when the API confirms creation, so a failure leaves the
   * entered content intact. HTTP 429 sets {@link rateLimitingError} and an HTTP 400
   * carrying `error.results` sets {@link noCustomerFound} with that text.
   */
  submitTicketForm() {
    this.ticketSubjectErrorMessage = ''
    this.participantsErrorMessage = ''
    this.ticketMessageErrorMessage = ''
    this.rateLimitingError = false;
    this.noCustomerFound = false;


    const emailRegex = /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,24}$/;
    const participants: string[] = this.participants
      ? this.participants.split(';').map(email => email.trim()).filter(email => email)
      : [];
    const allEmailsValid = participants.length === 0 || participants.every(email => emailRegex.test(email));

    if (this.ticketSubject === '') {
      this.ticketSubjectErrorMessage = 'Please provide ticket subject.'
    } else if (!allEmailsValid) {
      this.participantsErrorMessage = 'Please provide valid emails separated by semicolons.'
    } else if (this.ticketMessage === '') {
      this.ticketMessageErrorMessage = 'Please provide ticket description.'
    }
    if (this.ticketSubjectErrorMessage === '' && this.participantsErrorMessage === '' && this.ticketMessageErrorMessage === '' && this.fileUploadErrorMessage === '') {
      this.addNewTicket = true
      this.isSubmitting = true
      this.ticketService.createTicket(this.ticketSubject, participants, this.ticketMessage, this.uploadedFilesArray).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response.results === 'Ticket created successfully.') {
            this.ticketSubject = ''
            this.ticketIssue = ''
            this.participants = ''
            this.ticketMessage = ''
            this.uploadedFilesArray = []
            this.addNewTicket = false
          } else {
            this.addNewTicket = true
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.addNewTicket = true
          if (error.status === 429) {
            this.rateLimitingError = true;
          }
          console.error(error.status)
          if (error.status === 400 && error.error.results) {
            this.noCustomerFound = true;
              this.errorMessage = error.error.results;
          }
          console.error(error)
        }
      })
    }
  }

  /**
   * Switches the listing to a different ticket status, leaving sort mode.
   *
   * @param event The newly selected status value.
   */
  statusChange(event: any) {
    // this.getTicketsCount(event)
    this.showSortGrid = false
    this.isLoading = true
    this.selectedStatus = event

    this.ticketService.getTicketsData(event, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.data = this.formatRows(response.results);
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.page = 1


      },
      error: (error) => {
        this.data = []
        this.isLoading = false
        console.error(error)
      }
    })
  }

  /**
   * Applies a sort option and reloads the listing from the sorted endpoint.
   *
   * @param event Selected option as a `field-direction` string, e.g. `ticket_id-desc`.
   */
  sortData(event: any) {
    this.isLoading = true
    const [filter, order] = event.split('-')
    this.filter = filter
    this.order = order
    this.showSortGrid = true

    this.ticketService.sortTicketsData(filter, order, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.prevBtn = response.previous
        this.nextBtn = response.next
        this.data = this.formatRows(response.results);
        this.page = 1


      },
      error: (error) => {
        this.data = []
        this.isLoading = false
        console.error(error);
      }
    })
  }

  /**
   * Loads a ticket's email thread and hands it to the parent to open the detail view.
   *
   * The thread is also parked on `MessageStackService` for the detail view to read.
   * A "no ticket found" error is treated as the ticket not being assigned to this
   * user and sets {@link ticketNotAssigned}.
   *
   * @param ticket_id Ticket to open.
   */
  getTicketDetails(ticket_id: string) {

    this.ticketNotAssigned = false

    const ticket: any = this.data.find((item: any) => {
      return item.ticket_id === ticket_id
    })

    this.ticketService.getTicketDetails(ticket_id).subscribe({
      next: (response) => {
        this.messageStackService.messageStack = response
        this.messageStackService.ticketId = ticket_id
        // The row already holds the subject, status and CC list, none of which
        // the details endpoint returns, so they travel with the navigation
        // rather than being fetched again. A cold visit to the same URL has no
        // such state and rebuilds what it can from the thread - see
        // TicketDetailsComponent.
        this.router.navigate(['/ticket', ticket_id], {
          state: { ticket: { ...ticket, email_stack: response, cc_list: ticket.cc_list } }
        })
      },
      error: (error) => {
        if (error.error.results === `No ticket found with ID ${ticket_id}.`) {
          this.ticketNotAssigned = true
        }
      }
    })
  }

  /**
   * Loads the previous page of a status-filtered listing.
   *
   * Note: the parameter is named `nextPageURI` but receives the previous-page URL.
   *
   * @param nextPageURI The `previous` URL from the last response.
   * @param status Status the listing is filtered by.
   */
  prevPage(nextPageURI: string, status: string) {
    this.page = this.page - 1

    this.ticketService.nextTicketData(nextPageURI, status).subscribe({
      next: (response) => {
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.data = this.formatRows(response.results);

      }
    })

  }

  /**
   * Loads the next page of a sorted listing.
   *
   * @param nextPageURI The `next` URL from the last response.
   * @param filter Field being sorted on.
   * @param order Sort direction, `asc` or `desc`.
   */
  nextPageSort(nextPageURI: string,  filter: string, order: string) {
    this.page = this.page + 1

    this.ticketService.nextTicketDataSort(nextPageURI, filter, order).subscribe({
      next: (response) => {
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.data = this.formatRows(response.results);

      }
    })
  }

  /**
   * Loads the previous page of a sorted listing.
   *
   * @param prevPageURI The `previous` URL from the last response.
   * @param filter Field being sorted on.
   * @param order Sort direction, `asc` or `desc`.
   */
  prevPageSort(prevPageURI: string, filter: string, order: string) {
    this.page = this.page - 1

    this.ticketService.nextTicketDataSort(prevPageURI, filter, order).subscribe({
      next: (response) => {
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.data = this.formatRows(response.results);

      }
    })
  }

  /**
   * Loads the next page of a status-filtered listing.
   *
   * Unlike the sibling paging methods this one also stamps a `sr_no` on each row.
   *
   * @param nextPageURI The `next` URL from the last response.
   * @param status Status the listing is filtered by.
   */
  nextPage(nextPageURI: string, status: string) {
    this.page = this.page + 1

    this.ticketService.nextTicketData(nextPageURI, status).subscribe({
      next: (response) => {
        this.nextBtn = response.next
        this.prevBtn = response.previous
        this.data = this.formatRows(response.results);

      }
    })

  }

  /**
   * Normalises a listing's rows for display, rendering the timestamps the way
   * the design shows them.
   *
   * Rows are formatted BEFORE the array reaches the grid: assigning
   * `dataSource` first and mutating the row objects afterwards leaves the cells
   * that have already rendered showing the raw API values.
   *
   * @param results Raw `results` array from a listing response.
   * @returns The same rows, with display-ready dates.
   */
  private formatRows(results: any[]): any[] {
    const rows = results ?? [];

    rows.forEach((ticket: any, index: number) => {
      ticket['sr_no'] = index + 1;
      ticket.updated_at = this.displayDate(ticket.updated_at);
      ticket.created_at = this.displayDate(ticket.created_at);
    });

    return rows;
  }

  /**
   * Formats one timestamp for the grid. An unparseable value falls back to the
   * raw string instead of throwing, which would otherwise abandon the loop and
   * leave every later row on the page unformatted.
   */
  private displayDate(value: any): string {
    if (!value) return '';

    try {
      // 'yyyy' is the calendar year. 'YYYY' is the week-numbering year, which
      // Angular rejects outright (NG02300) unless the pattern also uses week
      // fields - that throw was landing in the catch below, so every cell in
      // both date columns quietly fell back to the raw API timestamp.
      return formatDate(value, 'dd-MMM-yyyy, HH:mm', 'en-US');
    } catch {
      return String(value);
    }
  }

  /**
   * Total pages in the current listing, from the row count and active page size.
   * Floors at 1 so an empty grid still reads "of 1" rather than "of 0".
   */
  get totalPages(): number {
    const size = Number(this.page_size);
    if (!this.totalCountPerStatus || !size) return 1;
    return Math.max(1, Math.ceil(this.totalCountPerStatus / size));
  }

  /** 1-based index of the first row on the current page; 0 when there are none. */
  get rangeStart(): number {
    return this.totalCountPerStatus ? (this.page - 1) * Number(this.page_size) + 1 : 0;
  }

  /** 1-based index of the last row on the current page. */
  get rangeEnd(): number {
    return Math.min(this.page * Number(this.page_size), this.totalCountPerStatus);
  }

  /**
   * Applies a listing response to the grid and normalises the displayed dates.
   *
   * @param response Paged response with `count`, `next`, `previous` and `results`.
   * @param target Page number the response represents.
   */
  private applyListing(response: any, target: number) {
    this.nextBtn = response.next;
    this.prevBtn = response.previous;
    this.data = this.formatRows(response.results);
    this.page = target;

    if (typeof response.count === 'number') {
      this.totalCountPerStatus = response.count;
    }

  }

  /**
   * Builds an absolute listing URL for an arbitrary page by rewriting the `page`
   * parameter on a pagination link the API already returned.
   *
   * The listing endpoints only hand back `next` and `previous`, so a jump needs
   * one of them to exist as a template. When neither does there is a single page
   * and nothing to jump to, so this returns null and the caller no-ops.
   */
  private pageUrl(target: number): string | null {
    const reference = this.nextBtn || this.prevBtn;
    if (!reference) return null;

    try {
      const url = new URL(reference);
      url.searchParams.set('page', String(target));
      url.searchParams.set('page_size', String(this.page_size));
      return url.toString();
    } catch {
      return null;
    }
  }

  /** Clamps a typed page number into the range the listing actually has. */
  private clampPage(raw: any): number {
    const parsed = Math.trunc(Number(raw));
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return Math.min(parsed, this.totalPages);
  }

  /** Jumps to the first page of the current status-filtered listing. */
  firstPage() {
    if (this.page === 1) return;

    this.ticketService.getTicketsData(this.selectedStatus, this.page_size).subscribe({
      next: (response) => this.applyListing(response, 1)
    });
  }

  /** Jumps to the last page of the current status-filtered listing. */
  lastPage() {
    this.jumpTo(this.totalPages);
  }

  /**
   * Jumps to a page typed into the pager's page box, clamping out-of-range
   * input and reflecting the clamped value back into the field.
   */
  goToPage(event: any) {
    const target = this.clampPage(event.target.value);
    event.target.value = target;
    this.jumpTo(target);
  }

  private jumpTo(target: number) {
    if (target === this.page) return;
    if (target === 1) {
      this.firstPage();
      return;
    }

    const url = this.pageUrl(target);
    if (!url) return;

    this.ticketService.nextTicketData(url, this.selectedStatus).subscribe({
      next: (response) => this.applyListing(response, target)
    });
  }

  /** Jumps to the first page of the current sorted listing. */
  firstPageSort() {
    if (this.page === 1) return;

    this.ticketService.sortTicketsData(this.filter, this.order, this.page_size).subscribe({
      next: (response) => this.applyListing(response, 1)
    });
  }

  /** Jumps to the last page of the current sorted listing. */
  lastPageSort() {
    this.jumpToSort(this.totalPages);
  }

  /** Sorted-listing counterpart to {@link goToPage}. */
  goToPageSort(event: any) {
    const target = this.clampPage(event.target.value);
    event.target.value = target;
    this.jumpToSort(target);
  }

  private jumpToSort(target: number) {
    if (target === this.page) return;
    if (target === 1) {
      this.firstPageSort();
      return;
    }

    const url = this.pageUrl(target);
    if (!url) return;

    this.ticketService.nextTicketDataSort(url, this.filter, this.order).subscribe({
      next: (response) => this.applyListing(response, target)
    });
  }

  constructor(private readonly elementRef: ElementRef, private readonly ticketService: TicketService, private readonly messageStackService: MessageStackService, private http: HttpClient, private readonly router: Router) { }

  /**
   * Reads the signed-in user from `sessionStorage` and loads the first page of
   * tickets for the default status filter.
   *
   * A 404 of "Customer user not found." sets {@link noCustomerFound} so the view can
   * explain the empty grid.
   */
  ngOnInit(): void {
    this.isLoading = true
    this.userEmail = window.sessionStorage.getItem('emailAddress')!
    this.userName = window.sessionStorage.getItem('userName')!
    this.ticketService.getTicketsData(this.selectedStatus, this.page_size).subscribe({
      next: (response) => {
        this.isLoading = false
        this.totalCountPerStatus = response.count
        this.prevBtn = response.previous
        this.nextBtn = response.next
        this.data = this.formatRows(response.results);
        this.page = 1



      },
      error: (error) => {
        if (error.status === 404 && error.error.results === 'Customer user not found.') {
          this.noCustomerFound = true;
        }
        this.data = []
        this.isLoading = false
        console.error(error)
      }  
    })  
  }

  /**
   * Clears the session and redirects to the logout page of the portal matching the
   * current hostname. Unrecognised hostnames clear the session but stay put.
   */
  logout() {
    sessionStorage.clear();
    const currentDomain = window.location.hostname;


    if (currentDomain === 'helpdesk.teamleaseregtech.com') {
      window.location.href = 'https://login.teamleaseregtech.com/logout.aspx';
    } else if (currentDomain === 'helpdesk.avantisregtec.in') {
      window.location.href = 'https://uat.avantisregtec.in/logout.aspx';
    } else if (currentDomain === 'impl.teamleaseregtech.com') {
      window.location.href = 'https://impl.teamleaseregtech.com/logout.aspx';
    } else if (currentDomain === 'demohelpdesk.teamleaseregtech.com') {
      window.location.href = 'https://demo.teamleaseregtech.com/logout.aspx';
    }
  }

  /** Opens the logout confirmation popup. */
  openLogOutComponent() {
    this.openLogoutPopup = true
  }

  /**
   * Closes the logout popup when a click lands outside it.
   *
   * @param event The document click being inspected.
   */
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    const openLogoutPopup = this.elementRef.nativeElement.querySelector('.log-out-popup')

    if (openLogoutPopup && !openLogoutPopup.contains(event.target)) {
      this.openLogoutPopup = false
    }
  }
}
